import React, { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import { Col, Container, Row, Card, CardBody, Button, Table, Alert, Badge } from "reactstrap";
import Loader from "../../components/Common/Loader";
import Select from "react-select";
import {
  getAgent,
  getCampaignFullList,
  getGestionesContactoRead,
  getReportsBot
} from "../../helpers/backend";
import ModalManagementDetails from "../Whatsapp/components/ModalManagementDetails";
import ReactApexChart from "react-apexcharts";
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_green.css';
import 'flatpickr/dist/l10n/es.js';
import "./Css/Report.css";

const ReportsBot = () => {
  // Estados
  const [detallesModal, setDetallesModal] = useState(false);
  const [gestionDetallada, setGestionDetallada] = useState(null);
  const [filters, setFilters] = useState({
    fechaInicio: "",
    fechaFin: "",
    estado: "",
    type: "",
    agente: "",
    campaña: "",
    isLead: false
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [showGraphic, setShowGraphic] = useState(false);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({
    agents: [],
    campaign: null,
    estado: null,
    type: null
  });
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Configuración del gráfico
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        type: 'donut',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      labels: ['En Bot', 'Fuera de Bot'],
      colors: ['#4CAF50', '#F44336'],
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '14px',
        markers: {
          width: 12,
          height: 12,
          radius: 6
        },
        itemMargin: {
          horizontal: 10,
          vertical: 5
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                color: '#333',
                fontSize: '16px',
                fontWeight: 600
              },
              value: {
                fontSize: '24px',
                fontWeight: 700,
                color: '#333'
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 600
        },
        dropShadow: {
          enabled: false
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 300
          },
          legend: {
            position: 'bottom'
          }
        }
      }],
      tooltip: {
        y: {
          formatter: function(val) {
            return val + " gestiones";
          }
        }
      }
    }
  });

  // Obtener datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [agentsRes, campaignsRes] = await Promise.all([
          getAgent(),
          getCampaignFullList()
        ]);
        
        setAgents(agentsRes.data);
        setCampaigns(campaignsRes.data);
        
        // Establecer fechas iniciales (hoy)
        const today = new Date().toISOString().split("T")[0];
        setFilters(prev => ({
          ...prev,
          fechaInicio: today,
          fechaFin: today
        }));
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  // Manejar cambios de fecha
  const handleDateChange = useCallback((selectedDates) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      setSelectedDate(selectedDates);
      setFilters(prev => ({
        ...prev,
        fechaInicio: start.toISOString().split("T")[0],
        fechaFin: end.toISOString().split("T")[0]
      }));
    }
  }, []);

  // Manejar cambios en los selects
  const handleSelectChange = useCallback((field, selectedOption) => {
    setSelectedOptions(prev => ({
      ...prev,
      [field]: selectedOption
    }));

    const value = Array.isArray(selectedOption) 
      ? selectedOption.map(opt => opt.value).join(",")
      : selectedOption?.value || "";

    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Obtener reporte
  const getReportData = useCallback(async () => {
    if (!filters.fechaInicio || !filters.fechaFin) {
      setShowWarning(true);
      return;
    }

    setLoading(true);
    setShowGraphic(false);

    try {
      const params = new URLSearchParams({
        fecha_inicio: filters.fechaInicio,
        fecha_fin: filters.fechaFin,
        estado: filters.estado,
        agents: filters.agente,
        campaign: filters.campaña,
        type: filters.type,
        is_leads: filters.isLead
      });

      const response = await getReportsBot(`?${params.toString()}`);
      setData(response.data);
      updateChartData(response.data);
      setShowGraphic(true);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
      setShowWarning(false);
    }
  }, [filters]);

  // Actualizar datos del gráfico
  const updateChartData = (data) => {
    const { gestiones_en_bot, gestiones_fuera_bot } = data;
    setChartData(prev => ({
      ...prev,
      series: [gestiones_en_bot, gestiones_fuera_bot],
      options: {
        ...prev.options,
        labels: [
          `En Bot (${gestiones_en_bot})`, 
          `Fuera de Bot (${gestiones_fuera_bot})`
        ]
      }
    }));
  };

  // Resetear búsqueda
  const resetSearch = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      estado: "",
      type: "",
      agente: "",
      campaña: "",
      isLead: false
    }));
    setSelectedDate(null);
    setSelectedOptions({
      agents: [],
      campaign: null,
      estado: null,
      type: null
    });
    setData({});
    setShowGraphic(false);
  }, []);

  // Manejar apertura de modal
  const handleGestionDetallada = async (id) => {
    try {
      const response = await getGestionesContactoRead(id);
      setGestionDetallada(response.data);
      setDetallesModal(true);
    } catch (error) {
      console.error("Error fetching gestion details:", error);
    }
  };

  return (
    <div className="page-content">
      <MetaTags>
        <title>Cuidad Limpia | Reporte de Bot</title>
      </MetaTags>
      <Container fluid>
        {/* Encabezado */}
        <div className="report-header">
          <h2 className="report-title">
            Reporte de Interacciones con Bot
          </h2>
          <p className="report-subtitle">
            Analiza el desempeño del bot y las interacciones manuales
          </p>
        </div>

        {/* Modal de detalles */}
        {detallesModal && (
          <ModalManagementDetails
            isOpen={detallesModal}
            onCloseClick={() => setDetallesModal(false)}
            GestionDetallada={gestionDetallada}
          />
        )}

        {/* Panel de filtros */}
        <Card className="filter-card shadow-sm">
          <CardBody>
            <Row className="g-3">
              <Col md={12} lg={2}>
                <label className="filter-label">Rango de fechas</label>
                <Flatpickr
                  className="form-control"
                  placeholder="Seleccione fechas"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d",
                    locale: "es",
                    maxDate: new Date()
                  }}
                  onChange={handleDateChange}
                  value={selectedDate}
                />
              </Col>

              <Col md={12} lg={2}>
                <label className="filter-label">Estado</label>
                <Select
                  onChange={(opt) => handleSelectChange("estado", opt)}
                  options={[
                    { value: "", label: "Todos" },
                    { value: "EN_GESTION", label: "En Gestión" },
                    { value: "VENCIDO", label: "Vencido" },
                    { value: "FINALIZADO", label: "Finalizado" },
                    { value: "PENDIENTE", label: "Pendiente" },
                  ]}
                  placeholder="Estado"
                  value={selectedOptions.estado}
                  classNamePrefix="select"
                  menuPosition="fixed"
                />
              </Col>

              <Col md={12} lg={2}>
                <label className="filter-label">Agentes</label>
                <Select
                  options={[
                    { label: "Todos", value: "" },
                    ...agents.map(agent => ({
                      label: agent.get_full_name,
                      value: agent.id,
                    }))
                  ]}
                  value={selectedOptions.agents}
                  onChange={(opt) => handleSelectChange("agente", opt)}
                  isMulti
                  placeholder="Agentes"
                  classNamePrefix="select"
                />
              </Col>

              <Col md={12} lg={2}>
                <label className="filter-label">Campañas</label>
                <Select
                  options={[
                    { label: "Todas", value: "" },
                    ...campaigns.map(campaign => ({
                      label: campaign.name,
                      value: campaign.id,
                    }))
                  ]}
                  onChange={(opt) => handleSelectChange("campaña", opt)}
                  value={selectedOptions.campaign}
                  placeholder="Campañas"
                  classNamePrefix="select"
                />
              </Col>

              <Col md={12} lg={2}>
                <label className="filter-label">Tipo</label>
                <Select
                  options={[
                    { value: "WhatsApp", label: "WhatsApp" },
                    { value: "Telefonía - Manual", label: "Telefonía Manual" }
                  ]}
                  onChange={(opt) => handleSelectChange("type", opt)}
                  value={selectedOptions.type}
                  placeholder="Tipo"
                  classNamePrefix="select"
                />
              </Col>

              <Col md={12} lg={2} className="d-flex align-items-end">
                <div className="d-flex w-100">
                  <Button
                    color="primary"
                    className="me-2 flex-grow-1"
                    onClick={getReportData}
                    disabled={loading}
                  >
                    {loading ? "Consultando..." : "Consultar"}
                  </Button>
                  <Button
                    outline
                    color="secondary"
                    className="flex-grow-1"
                    onClick={resetSearch}
                  >
                    Limpiar
                  </Button>
                </div>
              </Col>
            </Row>

            {showWarning && (
              <Alert color="warning" className="mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Por favor, seleccione un rango de fechas para continuar.
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* Resultados */}
        {loading ? (
          <Loader />
        ) : (
          showGraphic && (
            <>


              {/* Gráfico y tablas */}
              <Row className="mt-4">
                <Col lg={5}>
                  <Card className="shadow-sm">
                    <CardBody>
                      <h5 className="card-title d-flex align-items-center">
                        <i className="fas fa-chart-pie text-primary me-2"></i>
                        Distribución de Interacciones
                      </h5>
                      <div className="chart-container" style={{ height: "350px" }}>
                        <ReactApexChart
                          options={chartData.options}
                          series={chartData.series}
                          type="donut"
                          height="100%"
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <Badge color="success" className="me-2">
                          <i className="fas fa-circle me-1"></i> En Bot: {((data.gestiones_en_bot / data.total_gestiones) * 100 || 0).toFixed(1)}%
                        </Badge>
                        <Badge color="danger">
                          <i className="fas fa-circle me-1"></i> Fuera de Bot: {((data.gestiones_fuera_bot / data.total_gestiones) * 100 || 0).toFixed(1)}%
                        </Badge>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card className="shadow-sm">
                    <CardBody>
                      <ul className="nav nav-tabs nav-tabs-custom" role="tablist">
                        <li className="nav-item">
                          <a className="nav-link active" data-bs-toggle="tab" href="#en-bot" role="tab">
                            En Bot ({data.gestiones?.length || 0})
                          </a>
                        </li>
                        <li className="nav-item">
                          <a className="nav-link" data-bs-toggle="tab" href="#fuera-bot" role="tab">
                             Fuera de Bot ({data.gestiones_fuera_bot_list?.length || 0})
                          </a>
                        </li>
                      </ul>

                      <div className="tab-content mt-3">
                        <div className="tab-pane active" id="en-bot" role="tabpanel">
                          <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                            <Table hover className="table-nowrap align-middle mb-0 details-table">
                              <thead className="table-light">
                                <tr>
                                  <th>Contacto</th>
                                  <th>Estado</th>
                                  <th>Fecha</th>
                 
                                </tr>
                              </thead>
                              <tbody>
                                {data.gestiones?.map((gestion) => (
                                  <tr key={gestion.id} onClick={() => handleGestionDetallada(gestion.id)} className="clickable-row">
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div>
                                          <h6 className="mb-0">{gestion.contacto}</h6>
                                          <small className="text-muted">{gestion.campana?.[0]?.[0] || '-'}</small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                    <span
                                      className={`status-badge ${gestion.estado
                                        .toLowerCase()
                                        .replace(" ", "-")}`}
                                    >
                                      {gestion.estado}
                                    </span>
                                    </td>
                                    <td>{new Date(gestion.created_date).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>

                        <div className="tab-pane" id="fuera-bot" role="tabpanel">
                          <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                            <Table hover className="table-nowrap align-middle mb-0 details-table">
                              <thead className="table-light">
                                <tr>
                                  <th>Contacto</th>
                                  <th>Estado</th>
                                  <th>Fecha</th>
                         
                                </tr>
                              </thead>
                              <tbody>
                                {data.gestiones_fuera_bot_list?.map((gestion) => (
                                  <tr key={gestion.id} onClick={() => handleGestionDetallada(gestion.id)} className="clickable-row">
                                    <td>
                                      <div className="d-flex align-items-center" >
                                        <div>
                                          <h6 className="mb-0">{gestion.contacto}</h6>
                                          <small className="text-muted">{gestion.campana?.[0]?.[0] || '-'}</small>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                    <span
                                      className={`status-badge ${gestion.estado
                                        .toLowerCase()
                                        .replace(" ", "-")}`}
                                    >
                                      {gestion.estado}
                                    </span>
                                    </td>
                                    <td>{new Date(gestion.created_date).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )
        )}
      </Container>
    </div>
  );
};

export default ReportsBot;